import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ViewStatus } from '@uranda/ui';
import { ContactRequest, ContactStatus } from '../../../../../models/contact-request.model';
import { AdminService } from '../../services/admin.service';
import { PartnerService } from '../../services/partner.service';
import { ProfileService } from '../../../../../auth/services/profile.service';
import { Router } from '@angular/router';
import { ContactMotivation } from '../../../../../models/requests/createContactRequest.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationModalComponent } from '../../../components/confirmation-modal/confirmation-modal.component';
import { ContactRequestDetailsComponent } from '../contact-request-details/contact-request-details.component';
import { WalletService } from '../../../../../../services/wallet.service';
import { DateUtils } from '../../../../../utils/date-utils';

interface ContactStats {
  total: number;
  pending: number;
  accepted: number;
  refused: number;
  archived: number;
}

interface MotivationStats {
  appointment: number;
  info: number;
  offers: number;
}

import { HIDDEN_CONTACT_LABEL } from '../../../../../utils/partner-permissions.util';
@Component({
  selector: 'app-contacts-card',
  templateUrl: './contacts-card.component.html',
  styleUrls: ['./contacts-card.component.css'],
  standalone: false
})
export class ContactsCardComponent implements OnInit {
  readonly hiddenContactLabel = HIDDEN_CONTACT_LABEL;

  @ViewChild('confirmationModal')
  confirmationModal!: ConfirmationModalComponent;

  viewStatus = ViewStatus.LOADING;
  protected readonly ViewStatus = ViewStatus;
  isAdmin = false;
  stats: ContactStats = {
    total: 0,
    pending: 0,
    accepted: 0,
    refused: 0,
    archived: 0,
  };
  motivationStats: MotivationStats = {
    appointment: 0,
    info: 0,
    offers: 0,
  };
  recentContacts: ContactRequest[] = [];

  constructor(
    private adminService: AdminService,
    private partnerService: PartnerService,
    profileService: ProfileService,
    private router: Router,
    private modalService: NgbModal,
    private walletService: WalletService
  ) {
    this.isAdmin = profileService.isAdmin;
  }

  get isWalletDebtExceeded(): boolean {
    return this.walletService.isBlocked;
  }

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    try {
      this.viewStatus = ViewStatus.LOADING;

      const allContacts = await this.getContactRequests(0);

      this.recentContacts = allContacts.content || [];

      if (!this.isAdmin) {
        this.calculateMotivationStats();
      }

      this.viewStatus = ViewStatus.READY;
    } catch (error) {
      console.error('Error loading contacts data:', error);
      this.viewStatus = ViewStatus.READY;
    }
  }

  private async getContactRequests(page: number, status?: ContactStatus) {
    const filters = {};

    if (this.isAdmin) {
      return await this.adminService.getContactRequests(
        page,
        status,
        undefined,
        filters
      );
    } else {
      return await this.partnerService.getContactRequests(
        page,
        status,
        filters
      );
    }
  }

  navigateToDetails(contact: ContactRequest) {
    const modalRef = this.modalService.open(ContactRequestDetailsComponent, {
      backdrop: true,
      centered: false,
      size: 'lg',
      scrollable: true,
    });

    modalRef.componentInstance.contactRequestId = contact.id;

    modalRef.result
      .then(async (result) => {
        if (result) {
          await this.loadData();
        }
      })
      .catch(() => {
      });
  }

  calculateMotivationStats() {
    this.motivationStats = {
      appointment: 0,
      info: 0,
      offers: 0,
    };

    this.recentContacts.forEach((contact) => {
      switch (contact.motivation) {
        case ContactMotivation.APPOINTMENT:
          this.motivationStats.appointment++;
          break;
        case ContactMotivation.INFO:
          this.motivationStats.info++;
          break;
        case ContactMotivation.QUOTE:
          this.motivationStats.offers++;
          break;
      }
    });
  }

  getLocationText(contact: ContactRequest): string {
    return [contact.lead?.region].filter(Boolean).join(', ');
  }

  getStatusBadgeClass(status: ContactStatus): string {
    switch (status) {
      case ContactStatus.PENDING:
        return 'bg-warning text-white';
      case ContactStatus.ACCEPTED:
        return 'bg-success text-white';
      case ContactStatus.ARCHIVED:
        return 'bg-secondary text-white';
      default:
        return 'bg-secondary text-white';
    }
  }

  getStatusText(status: ContactStatus): string {
    switch (status) {
      case ContactStatus.PENDING:
        return 'Pending';
      case ContactStatus.ACCEPTED:
        return 'Accepted';
      case ContactStatus.ARCHIVED:
        return 'Archived';
      case ContactStatus.UNLOCKED:
        return 'Unlocked';
      case ContactStatus.REJECTED:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  }

  getMotivationText(motivation?: ContactMotivation): any {
    switch (motivation) {
      case ContactMotivation.APPOINTMENT:
        return 'Appointment';
      case ContactMotivation.INFO:
        return 'Informations';
      case ContactMotivation.QUOTE:
        return 'Quote';
      default:
        return motivation;
    }
  }

  getMotivationBadgeClass(motivation?: ContactMotivation): string {
    switch (motivation) {
      case ContactMotivation.APPOINTMENT:
        return 'bg-secondary text-white';
      case ContactMotivation.INFO:
        return 'bg-warning text-white';
      case ContactMotivation.QUOTE:
        return 'bg-success text-white';
      default:
        return 'bg-secondary text-white';
    }
  }

  formatDate(dateString: string): string {
    return DateUtils.formatDateTime(dateString);
  }

  selectedContact?: ContactRequest;

  showPhoneNumberConfirmation(contact: ContactRequest, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    this.selectedContact = contact;

    this.confirmationModal.open({
      title: 'Confirm Action',
      message: 'Are you sure you want to show the phone number?',
      submessage:
        "This action will accept the contact request and reveal the customer's phone number.",
      confirmText: 'Unlock',
      cancelText: 'Cancel',
      confirmButtonClass: 'btn-success',
      size: 'md',
      requirePhoneDigits: true,
    });
  }

  async unlockContactRequest(contact: ContactRequest, phoneDigits: string) {
    try {
      this.viewStatus = ViewStatus.LOADING;

      await this.partnerService.unlockPhoneNumber(contact.id.toString(), phoneDigits);
      await this.loadData();

      this.viewStatus = ViewStatus.READY;
    } catch (error) {
      console.error('Error accepting contact request:', error);
      this.viewStatus = ViewStatus.READY;
    }
  }

  onPhoneNumberConfirmed(data: { phoneDigits?: string }) {
    if (!this.selectedContact || !data.phoneDigits) return;
    this.unlockContactRequest(this.selectedContact, data.phoneDigits);
  }

  onPhoneNumberDismissed() {
    this.selectedContact = undefined;
  }

  navigateToFullPage() {
    const targetRoute = this.isAdmin ? '/admin/contact-requests' : '/partners/contact-requests';
    this.router.navigate([targetRoute]);
  }
}
